const OBJECT_PAYLOAD_TYPES = new Set([
    'checkbox',
    'ticket',
    'ticket-v2',
    'ticket-v2-single',
    'image-upload',
]);

const TICKET_TYPES = new Set(['ticket', 'ticket-v2', 'ticket-v2-single']);

const getFormElements = (form) => Array.isArray(form?.elements) ? form.elements : [];

const getResponses = (responses) =>
    responses && typeof responses === 'object' ? responses : {};

const answerMatches = (answer, optionId) => {
    if (answer === undefined || answer === null) return false;
    if (typeof answer === 'object') return Boolean(answer[optionId]);
    return String(answer) === String(optionId);
};

const createVisibilityResolver = (form, responses) => {
    const elements = getFormElements(form);
    const elementsById = new Map();
    elements.forEach((element) => {
        if (element?.id !== undefined && !elementsById.has(element.id)) {
            elementsById.set(element.id, element);
        }
    });

    const safeResponses = getResponses(responses);
    const visibilityByElement = new Map();
    const visiting = new Set();

    const resolve = (element) => {
        if (!element || element.deleted) return false;
        if (visibilityByElement.has(element)) return visibilityByElement.get(element);
        if (visiting.has(element)) return false;

        const showIf = element.showIf;
        if (!showIf) {
            visibilityByElement.set(element, true);
            return true;
        }

        const parent = elementsById.get(showIf.questionId);
        if (!parent || parent.deleted) {
            visibilityByElement.set(element, false);
            return false;
        }

        visiting.add(element);
        const visible = resolve(parent)
            && answerMatches(safeResponses[showIf.questionId], showIf.optionId);
        visiting.delete(element);
        visibilityByElement.set(element, visible);
        return visible;
    };

    return resolve;
};

export const isFormElementVisible = (element, form, responses = {}) =>
    createVisibilityResolver(form, responses)(element);

export const getHiddenFormElementIds = (form, responses = {}) => {
    const resolveVisibility = createVisibilityResolver(form, responses);
    return getFormElements(form)
        .filter((element) => !element.deleted && element.showIf)
        .filter((element) => !resolveVisibility(element))
        .map((element) => element.id);
};

export const clearHiddenFormValues = (form, responses = {}) => {
    const nextResponses = { ...getResponses(responses) };
    getHiddenFormElementIds(form, nextResponses).forEach((elementId) => {
        delete nextResponses[elementId];
    });
    return nextResponses;
};

export const createSubmitFormValues = (form, responses = {}) => {
    const nextResponses = { ...getResponses(responses) };
    const elementsById = new Map(getFormElements(form).map((element) => [element.id, element]));

    getHiddenFormElementIds(form, nextResponses).forEach((elementId) => {
        const element = elementsById.get(elementId);
        nextResponses[elementId] = OBJECT_PAYLOAD_TYPES.has(element?.type) ? {} : '';
    });
    return nextResponses;
};

export const getVisibleTicketElements = (form, responses = {}) => {
    const resolveVisibility = createVisibilityResolver(form, responses);
    return getFormElements(form)
        .filter((element) => !element.deleted && TICKET_TYPES.has(element.type))
        .filter(resolveVisibility);
};

export const getRequiredVisibleElements = (form, responses = {}, totalPriceToPay = 0) => {
    const resolveVisibility = createVisibilityResolver(form, responses);
    return getFormElements(form)
        .filter((element) => !element.deleted && element.required)
        .filter((element) => {
            if (totalPriceToPay !== 0 || element.isComprovant !== true) return true;
            return element.skipWhenNoPay === false;
        })
        .filter(resolveVisibility);
};

export const calculateVisibleTicketTotal = (form, responses = {}) => {
    const safeResponses = getResponses(responses);
    return getVisibleTicketElements(form, safeResponses)
        .reduce((total, element) => {
            if (element.type === 'ticket') {
                return total + (safeResponses[element.id] === 0 ? Number(element.price || 0) : 0);
            }

            return total + (element.content?.options || []).reduce((subtotal, option, index) => {
                if (option.hidden) return subtotal;
                const quantity = Number(safeResponses[element.id]?.[index] || 0);
                return subtotal + (quantity * Number(option.price || 0));
            }, 0);
        }, 0);
};
